import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LayoutDashboard, Users, Package, Search, Truck, LogOut, ShieldCheck, MessageSquare, Settings, Crown, Mail } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import logoImage from "@assets/Fill_The_Trucks_big_FullLogo_Transparent_NoBuffer_1770523528478.png";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const isRegularAdmin = user?.isAdmin === "true" || (user?.isAdmin as unknown) === true;
  const { data: adminSessionUser } = useQuery({
    queryKey: ["/api/admin/auth/me"],
    retry: false,
  });
  const isAdminSessionOnly = !!adminSessionUser && !isRegularAdmin;

  const handleExitAdmin = async () => {
    if (isAdminSessionOnly) {
      try {
        await apiRequest("POST", "/api/admin/auth/logout");
        queryClient.invalidateQueries({ queryKey: ["/api/admin/auth/me"] });
        setLocation("/admin-login");
      } catch (error) {
        console.error("Admin logout error:", error);
      }
    } else {
      setLocation("/");
    }
  };

  const navItems = [
    { href: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { href: "/admin/verification", label: "Verification", icon: ShieldCheck },
    { href: "/admin/users", label: "Users", icon: Users },
    { href: "/admin/loads", label: "Loads", icon: Package },
    { href: "/admin/load-board", label: "Public Load Board", icon: Search },
    { href: "/admin/trucks", label: "Trucks", icon: Truck },
    { href: "/admin/contact-submissions", label: "Contact", icon: MessageSquare },
    { href: "/admin/messages", label: "Messages", icon: Mail },
    { href: "/admin/admin-access", label: "Admin Access", icon: Crown },
    { href: "/admin/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="flex h-[100dvh] w-full">
      <aside className="w-64 border-r bg-card">
        <div className="flex h-full flex-col">
          <div className="border-b p-4">
            <Link href="/" className="flex items-center gap-3 hover-elevate rounded-md p-1 -m-1" data-testid="link-admin-logo-home">
              <img src={logoImage} alt="Fill The Trucks" className="h-10" />
            </Link>
            <p className="text-sm text-muted-foreground mt-2">Admin Panel</p>
          </div>
          
          <nav className="flex-1 space-y-1 p-4">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location === item.href;
              
              return (
                <Link key={item.href} href={item.href}>
                  <Button
                    variant={isActive ? "default" : "ghost"}
                    className={cn(
                      "w-full justify-start gap-3",
                      isActive && "bg-primary"
                    )}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                  </Button>
                </Link>
              );
            })}
          </nav>

          <div className="border-t p-4">
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              onClick={handleExitAdmin}
              data-testid="button-exit-admin"
            >
              <LogOut className="h-4 w-4" />
              {isAdminSessionOnly ? "Logout" : "Exit Admin"}
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        {children}
      </main>
    </div>
  );
}
